<template>
  <div class="p-6">
    <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">
      {{ page ? 'Edit Page' : 'New Page' }}
    </h2>

    <form @submit.prevent="handleSubmit" class="space-y-4">
      <UiControl id="page-title" label="Title *">
        <UiInput v-model="form.title" placeholder="About Us" />
      </UiControl>

      <UiControl id="page-slug" label="Slug *">
        <UiInput v-model="form.slug" placeholder="about-us" />
      </UiControl>

      <UiControl id="page-type" label="Page Type">
        <UiSelect
          id="page-type"
          v-model="form.page_type"
          :options="pageTypeOptions" />
      </UiControl>

      <UiControl id="page-status" label="Status">
        <UiSelect
          id="page-status"
          v-model="form.status"
          :options="statusOptions" />
      </UiControl>

      <div v-if="errors" class="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
        <p class="text-sm text-red-700 dark:text-red-400">
          {{ errors }}
        </p>
      </div>

      <div class="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
        <button
          type="button"
          @click="$emit('on-close-panel')"
          class="btn btn-secondary btn-sm">
          Cancel
        </button>
        <button
          type="submit"
          :disabled="saving"
          class="btn btn-primary btn-sm">
          {{ saving ? 'Saving...' : (page ? 'Update' : 'Create') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { createWebsitePage, updateWebsitePage } from '~/javascript/api/website_pages.js'
import UiInput from '~/javascript/components/ui/form/input/input.vue'
import UiSelect from '~/javascript/components/ui/form/select/select.vue'
import UiControl from '~/javascript/components/ui/form/control/control.vue'

const props = defineProps({
  page: { type: Object, default: null },
  websiteId: { type: String, required: true }
})

const emit = defineEmits(['on-close-panel', 'saved'])

const saving = ref(false)
const errors = ref(null)

const pageTypeOptions = [
  { id: 'page', title: 'Page' },
  { id: 'location_page', title: 'Location Page' },
  { id: 'service_page', title: 'Service Page' },
  { id: 'service_location_page', title: 'Service + Location' }
]

const statusOptions = [
  { id: 'draft', title: 'Draft' },
  { id: 'published', title: 'Published' },
  { id: 'archived', title: 'Archived' }
]

const defaultForm = { title: '', slug: '', page_type: 'page', status: 'published' }
const form = ref({ ...defaultForm })

const handleSubmit = async() => {
  try {
    saving.value = true
    errors.value = null

    if (props.page) {
      await updateWebsitePage(props.page.id, { website_page: form.value })
    } else {
      await createWebsitePage(props.websiteId, { website_page: form.value })
    }
    emit('saved')
  } catch (error) {
    if (error.response?.data?.details) {
      errors.value = Object.values(error.response.data.details).flat().join(', ')
    } else {
      errors.value = 'An error occurred. Please try again.'
    }
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  if (props.page) {
    Object.keys(defaultForm).forEach((key) => {
      form.value[key] = props.page[key] || defaultForm[key]
    })
  }
})
</script>
